import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Search, UserRound, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Patients() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [sortByEnd, setSortByEnd] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [form, setForm] = useState({
    name_kanji: "",
    name_kana: "",
    birth_date: "",
    gender: "",
    phone: "",
    address: "",
    main_diagnosis: "",
    care_level: "",
    primary_doctor_name: "",
    primary_medical_institution: "",
    additional_medical_institutions: [],
    doctor_phone: "",
  });

  const load = () => {
    api
      .get("/patients", { params: { q: q || undefined } })
      .then((r) => setItems(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
  }, [q]);

  useEffect(() => {
    api.get("/referrers").then((r) => setReferrers(r.data)).catch(() => {});
  }, []);

  const medicalReferrers = referrers.filter((r) =>
    ["医療機関", "病院", "クリニック"].includes(r.kind)
  );

  const applyReferrer = (refId) => {
    const r = referrers.find((x) => x.id === refId);
    if (!r) return;
    setForm({
      ...form,
      primary_medical_institution: r.name,
      primary_doctor_name: r.person_in_charge || form.primary_doctor_name || "",
      doctor_phone: r.phone || form.doctor_phone || "",
    });
    toast.success(`${r.name} を適用しました`);
  };

  const create = async () => {
    if (!form.name_kanji) {
      toast.error("氏名は必須です");
      return;
    }
    try {
      const r = await api.post("/patients", form);
      toast.success("利用者を登録しました");
      setOpen(false);
      setForm({ name_kanji: "", name_kana: "", birth_date: "", gender: "", phone: "", address: "", main_diagnosis: "", care_level: "", primary_doctor_name: "", primary_medical_institution: "", additional_medical_institutions: [], doctor_phone: "" });
      nav(`/patients/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "登録失敗");
    }
  };

  const startEdit = (p) => {
    setForm({
      name_kanji: p.name_kanji || "",
      name_kana: p.name_kana || "",
      birth_date: p.birth_date || "",
      gender: p.gender || "",
      phone: p.phone || "",
      address: p.address || "",
      main_diagnosis: p.main_diagnosis || "",
      care_level: p.care_level || "",
      primary_doctor_name: p.primary_doctor_name || "",
      primary_medical_institution: p.primary_medical_institution || "",
      additional_medical_institutions: Array.isArray(p.additional_medical_institutions) ? p.additional_medical_institutions : [],
      doctor_phone: p.doctor_phone || "",
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const update = async () => {
    if (!form.name_kanji) {
      toast.error("氏名は必須です");
      return;
    }
    try {
      await api.patch(`/patients/${editingId}`, form);
      toast.success("利用者情報を更新しました");
      setOpen(false);
      setEditingId(null);
      setForm({ name_kanji: "", name_kana: "", birth_date: "", gender: "", phone: "", address: "", main_diagnosis: "", care_level: "", primary_doctor_name: "", primary_medical_institution: "", additional_medical_institutions: [], doctor_phone: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "更新失敗");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`${p.name_kanji} さんを削除します。\n紐づく書類・記録・評価などはそのまま残りますが、利用者情報は完全に削除されます。\n続行しますか?`)) return;
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success(`${p.name_kanji} さんを削除しました`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "削除失敗");
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">PATIENTS</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">利用者管理</h1>
          <p className="text-[#4A4A48] mt-1">登録利用者 {items.length}名</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-patient-button" className="bg-[#2C5545] hover:bg-[#244538] text-white">
              <Plus size={16} className="mr-2" />新規登録
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? "利用者 情報編集" : "利用者 新規登録"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2 md:col-span-1">
                <Label>氏名(漢字)<span className="text-[#A74141]">*</span></Label>
                <Input data-testid="patient-name-input" value={form.name_kanji} onChange={(e) => setForm({ ...form, name_kanji: e.target.value })} placeholder="山田 太郎" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>氏名(カナ)</Label>
                <Input value={form.name_kana} onChange={(e) => setForm({ ...form, name_kana: e.target.value })} placeholder="ヤマダ タロウ" />
              </div>
              <div>
                <Label>生年月日</Label>
                <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} />
              </div>
              <div>
                <Label>性別</Label>
                <select
                  value={form.gender}
                  onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">選択</option>
                  <option>男</option>
                  <option>女</option>
                  <option>その他</option>
                </select>
              </div>
              <div>
                <Label>電話番号</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>要介護度</Label>
                <select
                  value={form.care_level}
                  onChange={(e) => setForm({ ...form, care_level: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">選択</option>
                  <option>要支援1</option><option>要支援2</option>
                  <option>要介護1</option><option>要介護2</option><option>要介護3</option><option>要介護4</option><option>要介護5</option>
                  <option>自立</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label>住所</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>主病名</Label>
                <Input value={form.main_diagnosis} onChange={(e) => setForm({ ...form, main_diagnosis: e.target.value })} />
              </div>

              {medicalReferrers.length > 0 && (
                <div className="col-span-2 p-3 rounded-lg bg-[#D9E2D5]/30 border border-[#D9E2D5]">
                  <Label className="text-xs text-[#2C5545]">医療機関マスタから選択(主治医・医療機関・電話を自動入力)</Label>
                  <select
                    data-testid="patient-create-referrer-select"
                    onChange={(e) => applyReferrer(e.target.value)}
                    defaultValue=""
                    className="mt-1 flex h-10 w-full rounded-md border border-[#E5E5E3] bg-white px-3 text-sm"
                  >
                    <option value="">医療機関を選択して自動入力...</option>
                    {medicalReferrers.map((r) => (
                      <option key={r.id} value={r.id}>
                        [{r.kind}] {r.name}{r.person_in_charge ? ` / ${r.person_in_charge}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label>主治医</Label>
                <Input value={form.primary_doctor_name} onChange={(e) => setForm({ ...form, primary_doctor_name: e.target.value })} />
              </div>
              <div>
                <Label>医療機関</Label>
                <select
                  data-testid="primary-medical-institution-select"
                  value={form.primary_medical_institution}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__custom__") return;
                    const ref = referrers.find((r) => r.name === v);
                    if (ref) {
                      setForm({
                        ...form,
                        primary_medical_institution: ref.name,
                        primary_doctor_name: ref.person_in_charge || form.primary_doctor_name,
                        doctor_phone: ref.phone || form.doctor_phone,
                      });
                    } else {
                      setForm({ ...form, primary_medical_institution: v });
                    }
                  }}
                  className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                >
                  <option value="">選択してください</option>
                  {referrers.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.kind ? `[${r.kind}] ` : ""}{r.name}{r.person_in_charge ? ` / ${r.person_in_charge}` : ""}
                    </option>
                  ))}
                  {form.primary_medical_institution && !referrers.some((r) => r.name === form.primary_medical_institution) && (
                    <option value={form.primary_medical_institution}>{form.primary_medical_institution} (マスタ未登録)</option>
                  )}
                </select>
              </div>
              <div className="col-span-2">
                <Label>主治医 電話</Label>
                <Input value={form.doctor_phone} onChange={(e) => setForm({ ...form, doctor_phone: e.target.value })} />
              </div>

              {/* 追加の医療機関(複数登録) */}
              <div className="col-span-2">
                <Label className="text-[#2C5545]">他の医療機関(複数登録可)</Label>
                <div className="mt-1 space-y-2">
                  {(form.additional_medical_institutions || []).length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="additional-institutions-list">
                      {form.additional_medical_institutions.map((inst, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D9E2D5]/40 border border-[#2C5545]/30 text-sm">
                          <div>
                            <div className="font-medium text-[#1C1C1A]">{inst.name}</div>
                            {(inst.doctor_name || inst.phone) && (
                              <div className="text-[10px] text-[#4A4A48]">
                                {inst.doctor_name || ""}{inst.doctor_name && inst.phone ? " / " : ""}{inst.phone || ""}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            data-testid={`remove-additional-institution-${i}`}
                            onClick={() => {
                              const next = [...form.additional_medical_institutions];
                              next.splice(i, 1);
                              setForm({ ...form, additional_medical_institutions: next });
                            }}
                            className="text-[#A74141] hover:bg-[#A74141]/10 rounded-full w-5 h-5 flex items-center justify-center"
                            title="削除"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <select
                    data-testid="add-additional-institution-select"
                    value=""
                    onChange={(e) => {
                      const refId = e.target.value;
                      if (!refId) return;
                      const r = referrers.find((x) => x.id === refId);
                      if (!r) return;
                      const list = form.additional_medical_institutions || [];
                      if (list.some((x) => x.name === r.name) || form.primary_medical_institution === r.name) {
                        toast.error(`${r.name} は既に登録されています`);
                        e.target.value = "";
                        return;
                      }
                      setForm({
                        ...form,
                        additional_medical_institutions: [
                          ...list,
                          { name: r.name, doctor_name: r.person_in_charge || "", phone: r.phone || "", kind: r.kind || "" },
                        ],
                      });
                      e.target.value = "";
                    }}
                    className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white"
                  >
                    <option value="">+ 他の医療機関を追加(医療機関マスタから選択)</option>
                    {referrers
                      .filter((r) => r.name !== form.primary_medical_institution &&
                                     !(form.additional_medical_institutions || []).some((x) => x.name === r.name))
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.kind ? `[${r.kind}] ` : ""}{r.name}{r.person_in_charge ? ` / ${r.person_in_charge}` : ""}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
              <Button data-testid="patient-save-button" className="bg-[#2C5545] hover:bg-[#244538] text-white" onClick={editingId ? update : create}>{editingId ? "保存" : "登録"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="patients-filter-bar">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A86]" />
          <Input
            data-testid="patient-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="氏名・カナ・病名で検索"
            className="pl-10 h-11 border-[#E5E5E3]"
          />
        </div>
        <div>
          <Label className="text-[10px] text-[#8A8A86]">契約状況</Label>
          <select
            data-testid="patient-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-11 px-3 text-sm rounded-md border border-[#E5E5E3] bg-white min-w-[140px]"
          >
            <option value="">全ての状況</option>
            {["契約中", "契約予定", "休止中", "一時中断", "入院中", "契約終了"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
            <option value="__none__">未設定</option>
          </select>
        </div>
        <button
          type="button"
          data-testid="patient-sort-end-toggle"
          onClick={() => setSortByEnd((v) => !v)}
          className={`h-11 px-3 text-xs rounded-md border transition ${
            sortByEnd
              ? "bg-[#2C5545] text-white border-[#2C5545]"
              : "bg-white text-[#2C5545] border-[#2C5545]"
          }`}
        >
          {sortByEnd ? "契約終了日順 ↑" : "契約終了日順"}
        </button>
      </div>

      <Card className="border-[#E5E5E3] bg-white overflow-hidden">
        {(() => {
          let filtered = items.filter((p) => {
            if (!statusFilter) return true;
            if (statusFilter === "__none__") return !p.contract_status;
            return p.contract_status === statusFilter;
          });
          if (sortByEnd) {
            filtered = [...filtered].sort((a, b) => {
              const ae = a.contract_end_date || "9999-12-31";
              const be = b.contract_end_date || "9999-12-31";
              return ae.localeCompare(be);
            });
          }
          return filtered.length === 0 ? (
          <div className="py-16 text-center text-[#8A8A86]">
            {items.length === 0 ? "利用者が登録されていません" : "条件に一致する利用者がいません"}
          </div>
        ) : (
          <>
            <div className="px-4 py-2 text-xs text-[#8A8A86] border-b border-[#E5E5E3] bg-[#FBFBF9]" data-testid="patients-result-count">
              該当 {filtered.length} / 全 {items.length} 件
            </div>
            <div className="divide-y divide-[#E5E5E3]">
            {filtered.map((p) => (
              <div
                key={p.id}
                data-testid={`patient-row-${p.id}`}
                className="w-full p-4 hover:bg-[#F3F3F1] flex items-center gap-4 transition group"
              >
                <button
                  type="button"
                  onClick={() => nav(`/patients/${p.id}`)}
                  className="flex items-center gap-4 flex-1 min-w-0 text-left"
                >
                  <div className="w-12 h-12 rounded-full bg-[#D9E2D5] flex items-center justify-center overflow-hidden shrink-0">
                    {p.photo_base64 ? (
                      <img src={p.photo_base64} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <UserRound size={22} className="text-[#2C5545]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#1C1C1A] truncate">{p.name_kanji}</div>
                    <div className="text-sm text-[#8A8A86] truncate">
                      {p.name_kana} {p.birth_date && `· ${p.birth_date}`} {p.main_diagnosis && `· ${p.main_diagnosis}`}
                    </div>
                  </div>
                  {p.care_level && (
                    <Badge variant="outline" className="border-[#D9E2D5] text-[#2C5545] shrink-0">{p.care_level}</Badge>
                  )}
                  {p.contract_status && (
                    <span
                      data-testid={`patient-contract-status-${p.id}`}
                      className={`shrink-0 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        p.contract_status === "契約中" ? "bg-[#2C5545] text-white" :
                        p.contract_status === "契約予定" ? "bg-[#4A6B8C] text-white" :
                        p.contract_status === "休止中" || p.contract_status === "一時中断" ? "bg-[#D4A050] text-white" :
                        p.contract_status === "入院中" ? "bg-[#A74141] text-white" :
                        p.contract_status === "契約終了" ? "bg-[#8A8A86] text-white" :
                        "bg-[#E5E5E3] text-[#1C1C1A]"
                      }`}
                    >
                      {p.contract_status}
                    </span>
                  )}
                  {p.contract_end_date && (
                    <span className="shrink-0 text-[10px] text-[#8A8A86] font-numeric">
                      ～{p.contract_end_date}
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-testid={`patient-view-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); nav(`/patients/${p.id}`); }}
                    title="詳細を見る"
                    className="h-8 w-8 text-[#4A4A48] hover:text-[#2C5545] hover:bg-[#D9E2D5]/40"
                  >
                    <Eye size={15} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-testid={`patient-edit-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); startEdit(p); }}
                    title="編集"
                    className="h-8 w-8 text-[#4A4A48] hover:text-[#2C5545] hover:bg-[#D9E2D5]/40"
                  >
                    <Pencil size={15} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-testid={`patient-delete-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); remove(p); }}
                    title="削除"
                    className="h-8 w-8 text-[#A74141] hover:text-white hover:bg-[#A74141]"
                  >
                    <Trash2 size={15} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          </>
        );
        })()}
      </Card>
    </div>
  );
}
